import { defaultTheme, useHelmet } from '@agro-club/agroclub-shared'
import useFormatDate from 'hooks/useFormatDate'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { Declaration } from 'modules/domain/types'
import { endpoints } from 'modules/endpoints'
import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import * as Layout from 'views/layouts/NewLayout'
import { DealAccordion } from 'views/pages/Deal/DealDetail/DealAccordion/DealAccordion'
import { DealDetailContent } from 'views/pages/Deal/DealDetail/DealDetailContent'
import DealRoutes from 'views/pages/Deal/routes'
import * as Header from 'views/ui/Header/Header'
import { AnalyticsPages, AnalyticsPlaces } from '../analyticsPlaces'
import { useDealContext } from './DealContext'
import { DealHeaderTags } from './DealHeaderTags'
import { DealManagement } from './DealManagement/DealManagement'
import { DetailMobile } from './Mobile/DetailMobile'
import { apiClient } from 'modules/utils/httpClient'

export const DealDetailWithProvider: FC = () => {
  const { t } = useTranslation('deal')
  const formatDate = useFormatDate()
  const { deal } = useDealContext()

  const [declarationProgress, declaration, declarationRefetch] = useSingleEntity<Declaration | undefined>(
    endpoints.dealDeclaration(deal.id),
  )

  useHelmet({ title: `${t('common:deal')} - ${deal?.product?.title}` })

  const isMobile = useMediaQuery({
    query: `(max-width: ${defaultTheme.queries.mobile})`,
  })

  useEffect(() => {
    // try to close task on page load if it exists
    apiClient.post(endpoints.closeDispatchTask(deal.id))
  }, [deal.id])

  return (
    <APageWrapper page={AnalyticsPages.DEAL_DETAIL} place={AnalyticsPlaces.DETAIL} id={deal.id}>
      {!isMobile ? (
        <Layout.WrapperContent>
          <Layout.Header>
            <Layout.TopHeader>
              <Header.Breadcrumbs
                routes={[
                  {
                    path: DealRoutes.List,
                    breadcrumbName: t('title'),
                  },
                  {
                    breadcrumbName: `${t('common:deal')} ID ${deal.id}`,
                  },
                ]}
              />
              <DealManagement />
            </Layout.TopHeader>

            <DealHeaderTags deal={deal} />

            <Layout.DetailPageSubTitle>
              {t('common:detailPageSubTitle', { id: deal.id, created: formatDate(deal.created, { withTime: true }) })}
            </Layout.DetailPageSubTitle>
          </Layout.Header>

          <Layout.ContentTwoColumns>
            <DealDetailContent declaration={declaration} />

            <DealAccordion
              declaration={declaration}
              declarationRefetch={declarationRefetch}
              declarationProgress={declarationProgress}
            />
          </Layout.ContentTwoColumns>
        </Layout.WrapperContent>
      ) : (
        <DetailMobile declaration={declaration} />
      )}
    </APageWrapper>
  )
}
